import { useEffect } from "react";

/**
 * Lightweight SEO helper.
 *
 * Sets:
 *   - <title>
 *   - <meta name="description">
 *   - <link rel="canonical">
 *   - OpenGraph tags (og:title, og:description, og:type, og:url, og:image)
 *   - Twitter card tags
 *   - Optional JSON-LD structured data (Article schema)
 *
 * All tags are restored / removed on unmount.
 */
export default function SEOHead({
  title,
  description,
  path,
  type = "website",
  image,
  jsonLd, // optional object — will be JSON.stringify'd into a <script type="application/ld+json">
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} — Just Care Community` : "Just Care Community";
    const prevTitle = document.title;
    document.title = fullTitle;

    const fullUrl = path
      ? `${window.location.origin}${path}`
      : window.location.href;

    const ensureMeta = (selector, attr, key, content) => {
      if (!content) return null;
      let tag = document.querySelector(selector);
      let created = false;
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
        created = true;
      }
      const prev = tag.getAttribute("content");
      tag.setAttribute("content", content);
      return { tag, prev, created };
    };

    const ensureLink = (rel, href) => {
      if (!href) return null;
      let tag = document.querySelector(`link[rel="${rel}"]`);
      let created = false;
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", rel);
        document.head.appendChild(tag);
        created = true;
      }
      const prev = tag.getAttribute("href");
      tag.setAttribute("href", href);
      return { tag, prev, created };
    };

    const tags = [];
    tags.push(ensureMeta('meta[name="description"]', "name", "description", description));
    tags.push(ensureMeta('meta[property="og:title"]', "property", "og:title", fullTitle));
    tags.push(ensureMeta('meta[property="og:description"]', "property", "og:description", description));
    tags.push(ensureMeta('meta[property="og:type"]', "property", "og:type", type));
    tags.push(ensureMeta('meta[property="og:url"]', "property", "og:url", fullUrl));
    tags.push(ensureMeta('meta[property="og:image"]', "property", "og:image", image));
    tags.push(ensureMeta('meta[name="twitter:card"]', "name", "twitter:card", image ? "summary_large_image" : "summary"));
    tags.push(ensureMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle));
    tags.push(ensureMeta('meta[name="twitter:description"]', "name", "twitter:description", description));
    tags.push(ensureMeta('meta[name="twitter:image"]', "name", "twitter:image", image));
    const canonical = ensureLink("canonical", fullUrl);

    // Optional JSON-LD structured data
    let ldScript = null;
    if (jsonLd) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.setAttribute("data-jcc-jsonld", "1");
      ldScript.text = JSON.stringify(jsonLd);
      document.head.appendChild(ldScript);
    }

    return () => {
      document.title = prevTitle;
      tags.forEach((t) => {
        if (!t) return;
        if (t.created) t.tag.remove();
        else if (t.prev !== null) t.tag.setAttribute("content", t.prev);
        else t.tag.removeAttribute("content");
      });
      if (canonical) {
        if (canonical.created) canonical.tag.remove();
        else if (canonical.prev !== null) canonical.tag.setAttribute("href", canonical.prev);
      }
      if (ldScript) ldScript.remove();
    };
  }, [title, description, path, type, image, jsonLd]);

  return null;
}
